<template>
  <div class="colour-picker n-input">
    <div class="colour-picker__wrapper">
      <label :for="inputId" class="colour-picker__label" :class="{ 'sr-only': hideLabel }">{{ label }}</label>
      <div class="colour-picker__input-wrapper">
        <input
          class="colour-picker__input"
          type="text"
          :id="inputId"
          v-model="currentColour"
          @blur="(e) => setColour((e.target as HTMLInputElement).value)"
          maxlength="7"
        />
        <div class="colour-picker__trigger">
          <div :id="id + '__trigger'" ref="pickerHost"></div>
        </div>
      </div>
      <span v-if="errorMessage" class="error-message">{{ errorMessage }}</span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
import Pickr from '@simonwep/pickr';
import { useIdGenerator } from '@/shared/designsystem/useIdGenerator';
import { useVModel } from '@vueuse/core';

defineOptions({
  name: 'n-colour-picker',
});

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  label: {
    type: String,
  },
  modelValue: {
    type: String,
    default: '#062B80',
  },
  errorMessage: {
    type: String,
  },
  hideLabel: {
    type: String,
  },
});

const { inputId } = useIdGenerator(props.id);

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
}>();

const currentColour = useVModel(props, 'modelValue', emit);

let pickr: Pickr | undefined;
const pickerHost = ref<HTMLElement | null>(null);

onMounted(() => {
  if (!pickerHost.value) {
    // If the host element isn't defined, just return.
    return;
  }
  pickr = Pickr.create({
    el: pickerHost.value,
    theme: 'monolith',
    lockOpacity: true,
    default: currentColour.value,
    position: 'bottom-start',

    components: {
      preview: false,
      opacity: false,
      hue: true,
      interaction: {
        clear: false,
        input: false,
        save: false,
      },
    },
  });

  pickr.on('change', (colour: any, instance: any) => {
    // fires 'save' event
    pickr?.applyColor();
  });

  pickr.on('save', (colour: any, instance: any) => {
    currentColour.value = colour.toHEXA().toString();
  });
});

onBeforeUnmount(() => {
  if (pickr) {
    pickr.destroyAndRemove();
  }
});

watch(currentColour, () => pickr?.setColor(currentColour.value));

function setColour(colour: string) {
  // fires 'change' event
  if (!pickr?.setColor(colour)) {
    currentColour.value = colour;
  }
}
</script>

<style src="@simonwep/pickr/dist/themes/monolith.min.css"></style>
<style src="@simonwep/pickr/dist/themes/classic.min.css"></style>
<style scoped lang="scss">
@use '../../../styles/accessibility' as *;
@use '../../../styles/colours' as *;
@use '../../../styles/input' as *;

.n-input {
  .colour-picker {
    &__input-wrapper {
      position: relative;
      max-width: 140px;
    }
    &__input {
      height: 45px;
      padding-bottom: 0.4rem;
      padding-left: 3rem;
    }
    &__trigger {
      position: absolute;
      left: 10px;
      top: 10px;
    }
  }
}
:deep(.pickr) {
  display: flex;
}
</style>
